import { InputAmountType } from "src/app/core/v2/types"
import { CostRevenue } from "../../types"
import { 
  NonRecoverable, 
  Recoverable, 
  totalRecoverable as calculateTR ,
  totalNonRecoverable as calculateTNR,
  mapCost
} from "../../utils"
import { basisMultiplier, yieldPurchaseOnPerpetuityAnnually, yieldPurchaseOnPerpetuityQuarterly } from "./utils"

export function calculateFreeholdCapOnPerpetuity(
  effectiveRent: number,
  item: Recoverable & NonRecoverable & {expensesInputAmountType: InputAmountType, sizeValue: number, rentBasis: {id: number}},
  capitalRate: number,
  purchaserCost: number,
  timingId: number,
) {
  const _basisMultiplier = basisMultiplier(item.rentBasis)
  const totalRecoverable = mapCost(calculateTR(item) * _basisMultiplier, item.expensesInputAmountType, item.sizeValue)

  const totalNonRecoverable = mapCost(calculateTNR(item) * _basisMultiplier, item.expensesInputAmountType, item.sizeValue)

  const passingRent = effectiveRent + totalRecoverable - totalNonRecoverable

  const yieldPurchaseOnPerpetuityAtCapitalRate = timingId === 1
    ? yieldPurchaseOnPerpetuityAnnually(capitalRate)
    : timingId === 2
      ? yieldPurchaseOnPerpetuityQuarterly(capitalRate)
      : undefined

  const capitalisedRent = yieldPurchaseOnPerpetuityAtCapitalRate * passingRent

  const totalPurchaserCost = (purchaserCost/100)  * capitalisedRent
  const value = capitalisedRent - totalPurchaserCost

  return {
    effectiveRent,
    passingRent,
    basisMultiplier: _basisMultiplier,
    recoverable: {
      total: totalRecoverable,
      recoverablePropertyManagement: mapCost(item.recoverablePropertyManagement * _basisMultiplier, item.expensesInputAmountType, item.sizeValue),
      recoverableLeasingExpenses: mapCost(item.recoverableLeasingExpenses * _basisMultiplier, item.expensesInputAmountType, item.sizeValue), 
      recoverableUtilities: mapCost(item.recoverableUtilities * _basisMultiplier, item.expensesInputAmountType, item.sizeValue),
      recoverableMaintenance: mapCost(item.recoverableMaintenance * _basisMultiplier, item.expensesInputAmountType, item.sizeValue),
      recoverableInsurance: mapCost(item.recoverableInsurance * _basisMultiplier, item.expensesInputAmountType, item.sizeValue),
      recoverableJanitorial: mapCost(item.recoverableJanitorial * _basisMultiplier, item.expensesInputAmountType, item.sizeValue),
      recoverablePropertyTaxes: mapCost(item.recoverablePropertyTaxes * _basisMultiplier, item.expensesInputAmountType, item.sizeValue),
      recoverableBusiness: mapCost(item.recoverableBusiness * _basisMultiplier, item.expensesInputAmountType, item.sizeValue),
      recoverableOthers: mapCost(item.recoverableOthers * _basisMultiplier, item.expensesInputAmountType, item.sizeValue)
    },
    nonRecoverable: {
      total: totalNonRecoverable,
      nonRecoverablePropertyManagement: mapCost(item.nonRecoverablePropertyManagement * _basisMultiplier, item.expensesInputAmountType, item.sizeValue),
      nonRecoverableLeasingExpenses: mapCost(item.nonRecoverableLeasingExpenses * _basisMultiplier, item.expensesInputAmountType, item.sizeValue),
      nonRecoverableUtilities: mapCost(item.nonRecoverableUtilities * _basisMultiplier, item.expensesInputAmountType, item.sizeValue),
      nonRecoverableMaintenance: mapCost(item.nonRecoverableMaintenance * _basisMultiplier, item.expensesInputAmountType, item.sizeValue),
      nonRecoverableInsurance: mapCost(item.nonRecoverableInsurance * _basisMultiplier, item.expensesInputAmountType, item.sizeValue),
      nonRecoverableJanitorial: mapCost(item.nonRecoverableJanitorial * _basisMultiplier, item.expensesInputAmountType, item.sizeValue),
      nonRecoverablePropertyTaxes: mapCost(item.nonRecoverablePropertyTaxes * _basisMultiplier, item.expensesInputAmountType, item.sizeValue),
      nonRecoverableBusiness: mapCost(item.nonRecoverableBusiness * _basisMultiplier, item.expensesInputAmountType, item.sizeValue),
      nonRecoverableOthers: mapCost(item.nonRecoverableOthers * _basisMultiplier, item.expensesInputAmountType, item.sizeValue)
    },
    yieldPurchaseOnPerpetuityAtCapitalRate,
    capitalisedRent: isNaN(capitalisedRent) ? undefined : capitalisedRent,
    totalPurchaserCost: isNaN(totalPurchaserCost) ? undefined : totalPurchaserCost,
    value: isNaN(value) ? undefined : value,
  }
}
